/**
 * Echo Web Client - TypeScript
 * Connects web applications to the Echo desktop debugging tool
 */

import type {
  EchoClientOptions,
  EchoConnection,
  EchoEventData,
  EchoEventType,
  EchoPlugin,
  EchoState,
} from '@cash-web/shared-echo-util-plugin'
import { createLogger } from '@cash-web/shared-echo-util-plugin'

export class EchoClient {
  private serverHost: string
  private serverPort: number
  private appIdentifier: string
  private deviceIdentifier: string

  private webSocket: WebSocket | null = null
  private state: EchoState = 'idle'
  public plugins: Map<string, EchoPlugin> = new Map()
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private intentionalDisconnect = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private eventListeners: Map<EchoEventType, Array<(data: any) => void>> = new Map()
  public readonly logger: ReturnType<typeof createLogger> = createLogger()

  constructor(options: EchoClientOptions = {}) {
    this.serverHost = options.serverHost ?? this.getLocalIP()
    this.serverPort = options.serverPort ?? 34143
    this.appIdentifier = options.appIdentifier ?? this.getAppIdentifier()
    this.deviceIdentifier = options.deviceIdentifier ?? this.getDeviceIdentifier()
    this.logger.setDebugMode(options.debug ?? false)
  }

  /**
   * Start the Echo client and connect to the desktop server
   */
  async start(): Promise<void> {
    if (this.state === 'connected' || this.state === 'connecting') {
      return
    }

    this.intentionalDisconnect = false
    await this.connect()
  }

  /**
   * Stop the Echo client and close connection
   */
  stop(): void {
    this.intentionalDisconnect = true
    if (this.webSocket) {
      this.webSocket.close()
      this.webSocket = null
    }
    this.state = 'idle'
  }

  /**
   * Add a plugin to the Echo client
   */
  addPlugin(plugin: EchoPlugin): void {
    if (this.plugins.has(plugin.id)) {
      this.logger.log('[Echo Debug] Plugin already registered:', plugin.id)
      return
    }

    this.logger.log('[Echo Debug] Adding plugin:', plugin.id)

    this.plugins.set(plugin.id, plugin)

    // If already connected, set up the plugin connection
    if (this.state === 'connected') {
      this.setupPluginConnection(plugin)
    }
  }

  /**
   * Remove a plugin from the Echo client
   */
  removePlugin(plugin: EchoPlugin): void {
    this.logger.log('[Echo Debug] Removing plugin:', plugin.id)
    this.plugins.delete(plugin.id)
    plugin.onDisconnect?.()
  }

  /**
   * Check if a plugin is registered
   */
  hasPlugin(pluginId: string): boolean {
    return this.plugins.has(pluginId)
  }

  /**
   * Add event listener
   */
  on<T extends EchoEventType>(event: T, callback: (data: EchoEventData[T]) => void): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, [])
    }
    this.eventListeners.get(event)?.push(callback)
  }

  /**
   * Enable/disable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.logger.setDebugMode(enabled)
    this.logger.log('[Echo Debug] Setting debug mode:', enabled)
  }

  /**
   * Set server host
   */
  setServerHost(host: string): void {
    this.serverHost = host
  }

  /**
   * Get current state
   */
  getState(): EchoState {
    return this.state
  }

  /**
   * Emit event - public method for plugins to emit events
   */
  public emit<T extends EchoEventType>(event: T, data: EchoEventData[T]): void {
    const listeners = this.eventListeners.get(event) || []
    listeners.forEach(callback => callback(data))
  }

  /**
   * Private: Establish WebSocket connection to Echo server
   */
  private async connect(): Promise<void> {
    const url = `ws://${this.serverHost}:${this.serverPort}/channel?deviceId=${encodeURIComponent(
      this.deviceIdentifier
    )}&appId=${encodeURIComponent(this.appIdentifier)}`
    this.state = 'connecting'
    this.emit('connecting', { url })

    try {
      this.webSocket = new WebSocket(url)

      this.webSocket.onopen = () => this.onOpen()
      this.webSocket.onclose = event => this.onClose(event)
      this.webSocket.onerror = error => this.onError(error)
      this.webSocket.onmessage = event => this.onMessage(event)
    } catch (error) {
      this.state = 'disconnected'
      this.emit('error', error as Error)
      this.scheduleReconnect()
    }
  }

  /**
   * Private: Handle WebSocket open event
   */
  private onOpen(): void {
    this.state = 'connected'
    this.reconnectAttempts = 0
    this.emit('connected', { host: this.serverHost, port: this.serverPort })

    // Send client info payload
    this.sendClientInfo()

    // Set up all registered plugins
    for (const plugin of this.plugins.values()) {
      this.setupPluginConnection(plugin)
    }
  }

  /**
   * Private: Handle WebSocket close event
   */
  private onClose(event: CloseEvent): void {
    this.state = 'disconnected'
    this.emit('disconnected', { code: event.code, reason: event.reason })

    // Notify all plugins
    for (const plugin of this.plugins.values()) {
      plugin.onDisconnect?.()
    }

    // Only attempt to reconnect if this was not an intentional disconnect
    if (!this.intentionalDisconnect) {
      this.scheduleReconnect()
    }
  }

  /**
   * Private: Handle WebSocket error event
   */
  private onError(error: Event): void {
    this.emit('error', error as unknown as Error)
  }

  /**
   * Private: Handle incoming WebSocket message
   */
  private async onMessage(event: MessageEvent): Promise<void> {
    try {
      // Handle both Blob and text data
      let data = event.data
      if (data instanceof Blob) {
        data = await data.text()
      }

      const payload = JSON.parse(data)

      // Handle both snake_case (plugin_id) and camelCase (pluginID) from server
      const pluginID = payload.plugin_id || payload.pluginID

      // Debug logging
      this.logger.log('[Echo Debug] Received message:', {
        payload,
        pluginID,
        hasPlugin: this.plugins.has(pluginID),
      })

      const plugin = this.plugins.get(pluginID)
      if (!plugin) {
        this.logger.log(
          '[Echo Debug] No plugin found for ID:',
          pluginID,
          'Available plugins:',
          Array.from(this.plugins.keys())
        )
        return
      }

      // Decode base64 data
      const decodedData = this.base64Decode(payload.data)

      this.logger.log('[Echo Debug] Decoded data for plugin', pluginID, ':', decodedData)

      // Check for lifecycle events
      if (decodedData === 'ClientPluginLifecycleEvent.onDesktopPluginActive') {
        plugin.onDesktopPluginActive?.()
        return
      }

      if (decodedData === 'ClientPluginLifecycleEvent.onDesktopPluginInactive') {
        plugin.onDesktopPluginInactive?.()
        return
      }

      // Regular data message
      plugin.onDataReceived?.(decodedData)
    } catch (error) {
      this.logger.error('[Echo Debug] Error processing message:', error)
      this.emit('error', error as Error)
    }
  }

  /**
   * Private: Send client information to Echo server
   */
  private sendClientInfo(): void {
    const clientInfo = {
      client_plugin_ids: Array.from(this.plugins.keys()),
    }

    const message = JSON.stringify(clientInfo)
    this.sendBinary(message)
  }

  /**
   * Private: Set up connection for a plugin
   */
  private setupPluginConnection(plugin: EchoPlugin): void {
    const connection: EchoConnection = {
      send: (data: string) => {
        const payload = {
          plugin_id: plugin.id,
          data: this.base64Encode(data),
        }

        // Debug logging
        this.logger.log(`[Echo Debug] Plugin ${plugin.id} sending:`, {
          rawData: data,
          payload,
          base64Data: payload.data,
        })

        this.sendBinary(JSON.stringify(payload))
      },
    }

    plugin.onConnect?.(connection)
  }

  /**
   * Private: Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('max_reconnect_attempts', undefined)
      return
    }

    this.reconnectAttempts++
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000)
    this.emit('reconnecting', { attempt: this.reconnectAttempts, delay })

    setTimeout(() => {
      this.connect()
    }, delay)
  }

  /**
   * Private: Get local IP address (defaults to 127.0.0.1 for local development)
   */
  private getLocalIP(): string {
    const hostname = window.location.hostname
    if (hostname === 'localhost' || hostname === '') {
      return '127.0.0.1'
    }
    return hostname
  }

  /**
   * Private: Generate app identifier from current page
   */
  private getAppIdentifier(): string {
    return window.location.hostname || 'unknown-web-app'
  }

  /**
   * Private: Generate device identifier
   */
  private getDeviceIdentifier(): string {
    let deviceId = localStorage.getItem('echo-device-id')
    if (!deviceId) {
      deviceId = `web-${crypto.randomUUID()}`
      localStorage.setItem('echo-device-id', deviceId)
    }
    return deviceId
  }

  /**
   * Private: Send binary data over WebSocket
   */
  private sendBinary(jsonString: string): void {
    if (!this.webSocket) return

    const encoder = new TextEncoder()
    const bytes = encoder.encode(jsonString)
    this.webSocket.send(bytes.buffer)
  }

  /**
   * Private: Base64 encode string
   */
  private base64Encode(str: string): string {
    const encoder = new TextEncoder()
    const data = encoder.encode(str)
    return btoa(String.fromCharCode(...data))
  }

  /**
   * Private: Base64 decode string
   */
  private base64Decode(str: string): string {
    const binaryString = atob(str)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    const decoder = new TextDecoder()
    return decoder.decode(bytes)
  }
}
